import {
  Flex,
  Button,
  Text,
  Icon,
  ModalManagerContext,
  RadioButton,
  RadioButtonGroup,
  Card,
} from '@pega/cosmos-react-core';
import { ModalMethods } from '@pega/cosmos-react-core/lib/components/Modal/Modal.types';
import { ColumnProps } from '@pega/cosmos-react-core/lib/components/Table/Table';
import React, { ChangeEvent, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { StyledTable } from '../../../Utilities/custom.styled';
import { CustomModal } from '../../../Utilities/CustomModal';
import AddChannel from '../Modals/AddChannel';
import { updateLoaderDetails, updateBannerInfo, setChatInfo, fetchAllAccounts } from '../../../../redux/actions';
import {
  addNewTransferConnection,
  deleteTransferConnection,
  loadMultichannel,
  updateChannelHopperPlacement,
} from '../ChatApis';
import { useNavigate } from 'react-router-dom';
import { accountInfo, appState, customModalProps, multiChannelRes } from '../../../../interfaces/common';
import { updateChannelHopperPlacementPayload } from '../../../../interfaces/chat';
import useLocalTranslation from '../../../../hooks/useLocalTranslation';

interface AddChannelInfoProps {
  availableChannels: multiChannelRes;
}

const AddChannelInfo: React.FC<AddChannelInfoProps> = (props) => {
  const { availableChannels } = props;
  const { connectionId } = useSelector((state: appState) => state.chatInfo);
  const { create } = useContext(ModalManagerContext);
  const modalMethods = useRef<ModalMethods<Partial<customModalProps>>>();
  const dispatch = useDispatch();
  const sessionId = useSelector((state: appState) => state.sessionId);
  const navigate = useNavigate();
  const translate = useLocalTranslation();

  const addChannelCallback = async (channelname: string, channelConnectionId: string) => {
    const payload = { connectionId: channelConnectionId };
    dispatch(updateLoaderDetails(true));
    const json = await addNewTransferConnection(sessionId, connectionId, payload);
    dispatch(updateLoaderDetails(false));
    if (json?.status === 200) {
      dispatch(setChatInfo(json?.data));
      dispatch(updateBannerInfo(`${channelname} ${translate('channel_added')}`, 'success'));
    } else if (json?.status === 403) {
      navigate('/');
      dispatch(updateBannerInfo(translate('invalid_session'), 'urgent'));
    } else {
      dispatch(updateBannerInfo(translate('channel_add_failed'), 'urgent'));
    }
  };

  return (
    <Flex
      container={{
        direction: 'row',
        justify: 'between',
      }}
    >
      <Flex>
        <Text variant="h3" as="h3">
          {translate('conversation_transfer')}
        </Text>
        <Text>{translate('channels_transferred_to_title_text')}</Text>
      </Flex>
      <Button
        disabled={!connectionId}
        onClick={() => {
          modalMethods.current = create(CustomModal, {
            heading: translate('add_channel'),
            children: <AddChannel addChannelCallback={addChannelCallback} availableChannels={availableChannels} />,
          });
        }}
      >
        {translate('add_channel')}
      </Button>
    </Flex>
  );
};

const ChannelHopper: React.FC = React.memo(() => {
  const {
    multichannel,
    connectionId,
    channelHopperPlacement = 'menu',
  } = useSelector((state: appState) => state.chatInfo);
  const [multiChannelDetails, setmultiChannelDetails] = useState<multiChannelRes>({});
  const { sessionId, accounts } = useSelector((state: appState) => {
    return {
      accounts: state.accounts,
      sessionId: state.sessionId,
    };
  });
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const translate = useLocalTranslation();

  const fetchMultiChannelDetails = useCallback(async () => {
    dispatch(updateLoaderDetails(true));
    let multiChannelDetails = await loadMultichannel(sessionId);
    dispatch(updateLoaderDetails(false));
    if (multiChannelDetails?.status === 200) {
      setmultiChannelDetails(multiChannelDetails?.data || {});
    } else {
      navigate('/');
      dispatch(
        updateBannerInfo(
          multiChannelDetails?.errorMessage ? translate(multiChannelDetails?.errorMessage) : '',
          'urgent',
        ),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, sessionId, navigate]);

  const [addedChannels, setAddedChannels] = useState<accountInfo[]>([]);
  const [availableChannels, setAvailableChannels] = useState<multiChannelRes>({});

  useEffect(() => {
    dispatch(fetchAllAccounts());
  }, [dispatch]);

  useEffect(() => {
    fetchMultiChannelDetails();
  }, [dispatch, fetchMultiChannelDetails]);

  useEffect(() => {
    // Setting added channels
    let tempAddedChannel: accountInfo[] = [];
    let addedPlatformNames: string[] = [];
    accounts?.map((connection) => {
      if (multichannel && multichannel.indexOf(connection.connectionId) !== -1) {
        tempAddedChannel.push(connection);
        addedPlatformNames.push(connection.standardPlatformName);
      }
    });
    setAddedChannels(tempAddedChannel);

    // Setting available channels
    let tempAvailableChannels: multiChannelRes = JSON.parse(JSON.stringify(multiChannelDetails));
    Object.keys(tempAvailableChannels)?.map((connectionName) => {
      if (addedPlatformNames.indexOf(connectionName) === -1) {
        let connectionLength = tempAvailableChannels[connectionName].length;
        while (connectionLength--) {
          let id = tempAvailableChannels[connectionName][connectionLength].connectionId;
          if (multichannel && multichannel.indexOf(id) !== -1) {
            tempAvailableChannels[connectionName].splice(connectionLength, 1);
          }
        }
      } else {
        tempAvailableChannels[connectionName] = [];
      }
    });
    setAvailableChannels(tempAvailableChannels);
  }, [multichannel, multiChannelDetails, accounts]);

  const deleteChannel = useCallback(
    async (connectionName: string, transferConnectionId: string) => {
      const payload = { connectionId: transferConnectionId };
      dispatch(updateLoaderDetails(true));
      const json = await deleteTransferConnection(sessionId, connectionId, payload);
      dispatch(updateLoaderDetails(false));
      if (json?.status === 200) {
        dispatch(setChatInfo(json?.data));
        dispatch(updateBannerInfo(`${connectionName} ${translate('channel_transfer_deleted')}`, 'success'));
      } else if (json?.status === 403) {
        navigate('/');
        dispatch(updateBannerInfo(translate('invalid_session'), 'urgent'));
      } else {
        dispatch(updateBannerInfo(translate('channel_transfer_delete_failed'), 'urgent'));
      }
    },
    [dispatch, sessionId, connectionId, translate, navigate],
  );

  const getColumnData = useMemo(() => {
    return addedChannels.map((item, index) => {
      return {
        id: index,
        channel: <Text>{translate(item.standardPlatformName)}</Text>,
        connection: (
          <>
            <Flex
              container={{
                direction: 'row',
                justify: 'between',
                alignItems: 'center',
              }}
            >
              <Text>{item.title}</Text>
              <Button
                variant="simple"
                icon
                onClick={() => {
                  deleteChannel(item.title, item.connectionId);
                }}
              >
                <Icon name="trash" />
              </Button>
            </Flex>
          </>
        ),
      };
    });
  }, [addedChannels, deleteChannel, translate]);

  const columns: ColumnProps<(typeof getColumnData)[number]>[] = [
    { renderer: 'channel', label: translate('channel') },
    { renderer: 'connection', label: translate('connection') },
  ];

  const updateChannelHopper = async (e: ChangeEvent<HTMLInputElement>) => {
    const placement = e.currentTarget.value;
    if (placement !== 'header' && placement !== 'menu') {
    }
    let payload: updateChannelHopperPlacementPayload = {
      channelHopperPlacement: placement,
    };
    dispatch(updateLoaderDetails(true));
    let json = await updateChannelHopperPlacement(sessionId, connectionId, payload);
    dispatch(updateLoaderDetails(false));
    if (json?.status === 200) {
      dispatch(setChatInfo(json?.data));
      dispatch(updateBannerInfo(translate('channel_switching_placement_updated'), 'success'));
    } else if (json?.status === 403) {
      navigate('/');
      dispatch(updateBannerInfo(translate('invalid_session'), 'urgent'));
    } else {
      dispatch(updateBannerInfo(translate('channel_switching_placement_update_failed'), 'urgent'));
    }
  };

  return (
    <Flex
      container={{
        direction: 'column',
        itemGap: 3,
      }}
    >
      <Flex
        container={{
          direction: 'column',
          itemGap: 1,
        }}
      >
        <Text variant="h3" as="h3">
          {translate('channel_switching_control_placement')}
        </Text>
        <RadioButtonGroup name="placement" onChange={updateChannelHopper}>
          <RadioButton label={translate('top_right_menu')} checked={channelHopperPlacement === 'menu'} value={'menu'} />
          <RadioButton
            label={translate('visible_in_header')}
            checked={channelHopperPlacement === 'header'}
            value={'header'}
          />
        </RadioButtonGroup>
      </Flex>
      <Flex
        container={{
          direction: 'column',
          itemGap: 1,
        }}
      >
        <AddChannelInfo availableChannels={availableChannels} />
        <Card style={{ padding: 0 }}>
          <StyledTable hoverHighlight={true} padding={'default'} columns={columns} data={getColumnData} />
        </Card>
      </Flex>
    </Flex>
  );
});

export default ChannelHopper;
